import type { AnalysisStatistics, AnalysisStatisticsEntryId } from '@/teoCore/TeoApi';
import type { TeoPDF } from '@/utils/teo-pdf/TeoPDF';
import type { Position } from '@/utils/teo-pdf/utils/types';

import {
  StatisticsTableDefinition,
  type Evaluation,
  type EvaluationType,
  type StatEntry,
} from './statistics.shape';
import { C } from './utils';

const seperatorIds = [
  'percent_long_words',
  'percent_long_sentences',
  'percent_abstracts',
  'percent_we',
];

type StatisticsTableData = {
  statistics: AnalysisStatistics;
  indexUsed: string[];
  extraEvaluations: Partial<Record<AnalysisStatisticsEntryId, Maybe<number>>>;
};
export function renderStatistics(
  pdf: TeoPDF,
  { statistics, indexUsed, extraEvaluations }: StatisticsTableData,
  pos: Position = pdf.position,
) {
  return pdf.createTable({
    defaultStyle: {
      border: 1,
      borderColor: C.Border,
    },
    columnStyles: [200, 100, '*'],
    rowStyles: (i) =>
      i === 0 ?
        {
          backgroundColor: C.Border,
          align: 'center',
          padding: { top: 8, bottom: 3, left: 8, right: 8 },
        }
      : {
          backgroundColor: 'white',
          padding: { top: 8, bottom: 3, left: 8, right: 8 },
        },
    position: pos,
    // @ts-expect-error -- pdfkit types are not correct
    createRows() {
      return [
        ['Parameter', 'Wert', 'Bewertung'],
        ...StatisticsTableDefinition.map(
          (stat: StatEntry<AnalysisStatisticsEntryId, AnalysisStatisticsEntryId>) => {
            const formattedValue = createFormattedValue(statistics, stat);
            if (formattedValue == null) return undefined;

            const evaluation = getEvaluation({ extraEvaluations, indexUsed, statistics }, stat);

            const isWithBorder = seperatorIds.includes(stat.id);
            const cellOptions = {
              border: {
                bottom: isWithBorder ? 12 : 2,
              },
              padding: {
                bottom: isWithBorder ? 12 : 3,
              },
            };

            return [
              {
                text: stat.label,
                ...cellOptions,
              },
              {
                text: formattedValue,
                textColor: evaluation.type === 'neutral' ? C.Text : C.TextOnDark,
                backgroundColor: backgroundColor(evaluation.type),
                ...cellOptions,
              },
              {
                text: evaluation.label ? `<<< ${evaluation.label}` : '',
                textColor: textColor(evaluation.type),
                ...cellOptions,
              },
            ];
          },
        ).filter((x) => !!x),
      ];
    },
  });
}

function backgroundColor(type: EvaluationType) {
  if (type === 'good') {
    return C.Good;
  }
  if (type === 'warn') {
    return C.Warn;
  }
  if (type === 'bad') {
    return C.Bad;
  }
  return 'white';
}

function textColor(type: EvaluationType) {
  if (type === 'good') {
    return C.GoodDark;
  }
  if (type === 'warn') {
    return C.WarnDark;
  }
  if (type === 'bad') {
    return C.BadDark;
  }
  return 'black';
}

function formatValue(type: string = 'int', value: number) {
  if (value == null || isNaN(value)) {
    return 'N/A';
  }
  switch (type) {
    case 'int':
      return String(Math.round(value));
    case 'float-2':
      return value.toFixed(2);
    case 'percentage':
      return `${value.toFixed(2)}%`;
  }
}

function createFormattedValue(
  statistics: StatisticsTableData['statistics'],
  stat: StatEntry<AnalysisStatisticsEntryId, AnalysisStatisticsEntryId>,
) {
  const value = statistics[stat.id];
  if (value == null) return null;

  const secondaryValue = stat.secondaryId && statistics[stat.secondaryId];
  const formattedFirstValue = formatValue(stat.type, value);
  const formattedSecondaryValue = secondaryValue && formatValue(stat.type, secondaryValue);
  return secondaryValue ?
      `${formattedFirstValue} – ${formattedSecondaryValue}`
    : formattedFirstValue;
}

function getEvaluation(
  { extraEvaluations, indexUsed, statistics }: StatisticsTableData,
  stat: StatEntry<AnalysisStatisticsEntryId, AnalysisStatisticsEntryId>,
): { type: EvaluationType; label: string } {
  const value = statistics[stat.id];
  const evalValue = extraEvaluations[stat.id] ?? value;
  if (evalValue == null) return { type: 'neutral', label: '' };

  const matchedEvaluation =
    (indexUsed.includes(stat.evalIndex ?? '') &&
      stat.evaluations?.find((e: Evaluation) => evalValue < (e.max ?? Infinity))) ||
    null;

  return {
    type: matchedEvaluation?.type ?? 'neutral',
    label: matchedEvaluation?.label ?? '',
  };
}
